import { useEffect, useState } from 'react';

/**
 * True once a surface is SHOWN and its layout has been quiet for `frames`
 * animation frames; false while it is hidden and again for those frames after
 * anything that moves its geometry without remounting it.
 *
 * Why it exists (owner, 27.10.2026: "switching from documents to tasks makes
 * the tasks not render", and the same after Ctrl + / Ctrl −): the board's
 * cards used `content-visibility: auto`, which lets the ENGINE decide, frame
 * by frame, whether a card is near the viewport and gets painted. That
 * decision is made asynchronously and goes stale when its inputs change
 * behind its back:
 *   · a kept-alive page goes `display: none` (EditorPane hides the board while
 *     Documents is open) and comes back — the cards return still "skipped";
 *   · content zoom (CSS `zoom`, utils/appZoom.js) rescales the board — WebKit
 *     collapsed visible cards to 0×0, Chromium dropped them for a frame.
 * A skipped card keeps its reserved height and paints nothing: a blank board.
 *
 * So the optimisation is only switched on when this says the layout is
 * settled, and switched off — every card painted normally — whenever the page
 * is hidden, shown, zoomed or resized. Re-applying it to cards that are
 * already laid out on screen makes the engine decide afresh from real
 * geometry, which is the one case it gets right.
 *
 * What counts as "moved": `active` flipping, a window `resize` (useAppZoom
 * dispatches one after every zoom step), the content zoom attribute on <html>,
 * and a devicePixelRatio change (window zoom / moving to another monitor).
 */
export const SETTLE_FRAMES = 2;

export function useSettledLayout(active, { frames = SETTLE_FRAMES } = {}) {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!active) {
      setSettled(false);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (cb) => window.setTimeout(cb, 16);
    const cancelRaf = window.cancelAnimationFrame
      ? window.cancelAnimationFrame.bind(window)
      : (id) => window.clearTimeout(id);

    let handle = null;
    let alive = true;
    const settle = () => {
      if (handle != null) cancelRaf(handle);
      let left = Math.max(1, frames);
      const tick = () => {
        if (!alive) return;
        left -= 1;
        if (left <= 0) {
          handle = null;
          setSettled(true);
        } else {
          handle = raf(tick);
        }
      };
      handle = raf(tick);
    };
    const unsettle = () => {
      setSettled(false);
      settle();
    };

    unsettle();
    window.addEventListener('resize', unsettle);

    let zoomObserver = null;
    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      zoomObserver = new MutationObserver(unsettle);
      zoomObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-app-zoom'] });
    }

    // devicePixelRatio has no event of its own: a media query for the
    // CURRENT ratio stops matching when it changes, then re-arm for the new one.
    let dprQuery = null;
    const onDpr = () => {
      armDpr();
      unsettle();
    };
    const armDpr = () => {
      if (dprQuery) dprQuery.removeEventListener?.('change', onDpr);
      dprQuery = null;
      if (typeof window.matchMedia !== 'function') return;
      const q = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      if (!q || typeof q.addEventListener !== 'function') return;
      q.addEventListener('change', onDpr);
      dprQuery = q;
    };
    armDpr();

    return () => {
      alive = false;
      if (handle != null) cancelRaf(handle);
      window.removeEventListener('resize', unsettle);
      zoomObserver?.disconnect();
      dprQuery?.removeEventListener?.('change', onDpr);
    };
  }, [active, frames]);

  return active && settled;
}

export default useSettledLayout;
